import React from "react";
import { Route, Navigate } from "react-router-dom";
import MainLayout from "../../components/MainLayout/MainLayout";
import { AuthenticationGuard } from "@/components/AuthenticationGuard/AuthenticationGuard";
import PermissionGuard from "../../components/PermissionGuard/PermissionGuard";
import HubDashboard from "../../modules/hub/features/HubDashboard/HubDashboard";
import MonthlyBalanceSheet from "../../modules/hub/features/FinancialReports/components/MonthlyBalanceSheet/MonthlyBalanceSheet";
import ProfitAndLoss from "../../modules/hub/features/FinancialReports/components/ProfitAndLoss/ProfitAndLoss";
import ProfitAndLossBuilder from "../../modules/hub/features/FinancialReports/components/ProfitAndLossBuilder/ProfitAndLossBuilder";
import TransactionList from "../../modules/hub/features/FinancialReports/components/TransactionList/TransactionList";
import UserList from "../../modules/hub/features/UserManagement/components/UserList/UserList";
import EditUser from "../../modules/hub/features/UserManagement/components/EditUser/EditUser";
import CreateUser from "../../modules/hub/features/UserManagement/components/CreateUser/CreateUser";
import SettingsLayout from "../../modules/hub/features/UserAppSettings/components/SettingsLayout/SettingsLayout";
import UserProfile from "../../modules/hub/features/UserAppSettings/components/UserProfile/UserProfile";
import CompanyProfile from "../../modules/hub/features/UserAppSettings/components/CompanyProfile/CompanyProfile";
import SubscriptionSettings from "../../modules/hub/features/UserAppSettings/components/SubscriptionSettings/SubscriptionSettings";
import AnnualRevenue from "../../modules/hub/features/UserAppSettings/components/AnnualRevenue/AnnualRevenue";
import Integrations from "../../modules/hub/features/UserAppSettings/components/Integrations/Integrations";
import CoachingDashboard from "@/modules/hub/features/CoachingDashboard/components/CoachingDasboard/CoachingDashboard";
import { ResourcesLibrary as ResourceLibrary } from "@/modules/hub/features/ResourceLibrary/components/ResourceLibrary/ResourceLibrary";
import { ResourcesLibraryV2 as ResourceLibraryV2 } from "@/modules/hub/features/ResourceLibraryV2/components/ResourceLibraryV2/ResourceLibraryV2";
import ResourceDetails from "@/modules/hub/features/ResourceLibrary/components/ResourceDetails/ResourceDetails";
import ResourceDetailsV2 from "@/modules/hub/features/ResourceLibraryV2/components/ResourceDetails/ResourceDetails";
import { Podcasts } from "@/modules/hub/features/Podcasts/components/Podcasts/Podcasts";
import { PodcastsV2 } from "@/modules/hub/features/PodcastsV2/components/PodcastsV2/PodcastsV2";
import { PartnerNetwork } from "@/modules/hub/features/PartnerNetwork/components/PartnerNetwork/PartnerNetwork";
import { PartnerNetworkV2 } from "@/modules/hub/features/PartnerNetworkV2/components/PartnerNetworkV2/PartnerNetworkV2";
import { News } from "@/modules/hub/features/News/components/News/News";
import { NewsV2 } from "@/modules/hub/features/NewsV2/components/NewsV2/NewsV2";
import LeadGeneration from "@/modules/hub/features/LeadGeneration/components/LeadGeneration/LeadGeneration";
import FieldCoachPage from "@/modules/hub/features/FieldCoach/components/FieldCoachPage/FieldCoachPage";
import StageFeatureMatrix from "@/modules/hub/features/StageFeatureMatrix/components/StageFeatureMatrix/StageFeatureMatrix";
import permissionsService from "@/services/permissionsService";
// Create stable component reference
const HubMainLayout = () => <MainLayout section="hub" />;

// Hub home: users with the "resources" permission see the dashboard; everyone
// else is redirected to the Lead Generation page (their available landing).
const HubHome = () => {
  const { hasPermission } = permissionsService();

  return hasPermission("resources") ? (
    <HubDashboard />
  ) : (
    <Navigate replace to="/hub/lead-generation" />
  );
};

/**
 * HubRoutes
 *
 * Wraps the /hub section in the Auth0-based authentication guard, then
 * protects individual routes with PermissionGuard based on the required
 * permissions from your navigation config.
 */
const HubRoutes = (
  <Route
    element={<AuthenticationGuard component={HubMainLayout} />}
    path="/hub"
  >
    <Route element={<HubHome />} index />

    <Route element={<CoachingDashboard />} path="dashboards/coaching" />

    {/* Financial Reports routes */}
    <Route
      element={
        <PermissionGuard
          requiredPermissions={["financial-reports-monthly-balance"]}
        >
          <MonthlyBalanceSheet />
        </PermissionGuard>
      }
      path="financial-reports/monthly-balance-sheet"
    />
    <Route
      element={
        <PermissionGuard
          requiredPermissions={["financial-reports-profit-and-loss"]}
        >
          <ProfitAndLoss />
        </PermissionGuard>
      }
      path="financial-reports/profit-and-loss"
    />
    <Route
      element={
        <PermissionGuard
          requiredPermissions={["financial-reports-p-and-l-report-builder"]}
        >
          <ProfitAndLossBuilder />
        </PermissionGuard>
      }
      path="financial-reports/profit-and-loss-builder"
    />
    <Route
      element={
        <PermissionGuard
          requiredPermissions={["financial-reports-transaction-list"]}
        >
          <TransactionList />
        </PermissionGuard>
      }
      path="financial-reports/transaction-list"
    />

    {/* User Management routes - accessible via user-management permission */}
    <Route
      element={
        <PermissionGuard requiredPermissions={["user-management"]}>
          <UserList />
        </PermissionGuard>
      }
      path="users"
    />
    <Route
      element={
        <PermissionGuard requiredPermissions={["user-management"]}>
          <CreateUser />
        </PermissionGuard>
      }
      path="users/create"
    />
    <Route
      element={
        <PermissionGuard requiredPermissions={["user-management"]}>
          <EditUser />
        </PermissionGuard>
      }
      path="users/:uuid/edit"
    />

    {/* Settings - nested routes with side navigation */}
    <Route element={<SettingsLayout />} path="settings">
      <Route element={<Navigate replace to="profile" />} index />
      <Route element={<UserProfile />} path="profile" />
      <Route
        element={
          <PermissionGuard requiredPermissions={["company-profile"]}>
            <CompanyProfile />
          </PermissionGuard>
        }
        path="company"
      />
      <Route
        element={
          <PermissionGuard requiredPermissions={["company-profile"]}>
            <SubscriptionSettings />
          </PermissionGuard>
        }
        path="subscription"
      />
      <Route
        element={
          <PermissionGuard requiredPermissions={["integrations"]}>
            <Integrations />
          </PermissionGuard>
        }
        path="integrations"
      />
      <Route
        element={
          <PermissionGuard requiredPermissions={["integrations"]}>
            <Integrations />
          </PermissionGuard>
        }
        path="integrations/servicetitan"
      />
      <Route
        element={
          <PermissionGuard requiredPermissions={["integrations"]}>
            <Integrations />
          </PermissionGuard>
        }
        path="integrations/quickbooks"
      />
      <Route
        element={
          <PermissionGuard requiredPermissions={["annual-revenue"]}>
            <AnnualRevenue />
          </PermissionGuard>
        }
        path="annual-revenue"
      />
    </Route>

    <Route
      element={
        <PermissionGuard requiredPermissions={["resources"]}>
          <ResourceLibrary />
        </PermissionGuard>
      }
      path="resources"
    />
    <Route
      element={
        <PermissionGuard requiredPermissions={["resources"]}>
          <ResourceLibraryV2 />
        </PermissionGuard>
      }
      path="resources-v2"
    />
    <Route
      element={
        <PermissionGuard requiredPermissions={["resources"]}>
          <ResourceDetailsV2 />
        </PermissionGuard>
      }
      path="resources-v2/:slug"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["resources"]}>
          <ResourceDetails />
        </PermissionGuard>
      }
      path="resources/:slug"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["resources"]}>
          <News />
        </PermissionGuard>
      }
      path="news"
    />
    <Route
      element={
        <PermissionGuard requiredPermissions={["resources"]}>
          <NewsV2 />
        </PermissionGuard>
      }
      path="news-v2"
    />
    <Route
      element={
        <PermissionGuard requiredPermissions={["resources"]}>
          <Podcasts />
        </PermissionGuard>
      }
      path="podcasts"
    />
    <Route
      element={
        <PermissionGuard requiredPermissions={["resources"]}>
          <PodcastsV2 />
        </PermissionGuard>
      }
      path="podcasts-v2"
    />
    <Route
      element={
        <PermissionGuard
          requiredPermissions={["partner-network", "partner-network-admin"]}
        >
          <PartnerNetwork />
        </PermissionGuard>
      }
      path="partner-network"
    />
    <Route
      element={
        <PermissionGuard
          requiredPermissions={["partner-network", "partner-network-admin"]}
        >
          <PartnerNetworkV2 />
        </PermissionGuard>
      }
      path="partner-network-v2"
    />

    <Route element={<LeadGeneration />} path="lead-generation" />

    <Route element={<FieldCoachPage />} path="fieldcoach" />

    <Route element={<StageFeatureMatrix />} path="growth-stages" />
  </Route>
);

export default HubRoutes;
